import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import { Fonts } from '@paperflite-marketing/cleverstory-commons';

import { FORM_STEP } from '../../../constants/widgetTypes';
import { FormContext, WidgetsContext } from '../core/context';
import { HubspotFormView } from './hubspot';
import { MarketoFormView } from './marketo';
import { ExternalForm } from './external';
import { injectAktivFallback } from '../widget.helper';

import styles from './style.module.scss';
import { PardotFormView } from './pardot';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';


export const ExternalFormWidgetContext = React.createContext({
  onFormSubmit: () => { }
});

const ExternalFormWidget = function ExternalFormEditor({ className, style, widgetProps, breakpoint, children }) {
  const context = React.useContext(WidgetsContext);
  const externalFormContext = React.useContext(FormContext);

  const [externalFormState, setExternalFormState] = useState({
    formStyles: {},
    step: ''
  });

  const [externalFormContextState, setExternalFormContextState] = useState({
    parent: null
  })

  const id = get(widgetProps, 'uid');
  const formSuccess = get(widgetProps, 'success', false);

  var formSteps = children.filter(item => get(item, 'props.widgetProps.type') === FORM_STEP)
    .map(item => get(item, 'props.widgetProps'));

  formSteps.push(widgetProps)
  formSteps = orderBy(formSteps, ['order'], ['asc']);

  const properties = get(widgetProps, 'properties', {});
  const deviceProperties = get(properties, `${breakpoint}`, {});

  Fonts.useFont(get(widgetProps, 'fonts') || []);

  var widgetStyle = {
    ...style,
    ...externalFormState.formStyles
  }

  useEffect(() => {
    if (formSuccess) {
      const steps = orderBy(formSteps, ['order'], ['desc']);
      const successStep = steps[1];
  
      setExternalFormState(s => ({
        ...s,
        step: get(widgetProps, 'step', get(successStep, 'uid', null))
      }));
    }
  }, [formSuccess]);

  const handleFormSubmit = (contactRequest, parent) => {

    formSteps = orderBy(formSteps, ['order'], ['desc']);

    const steps = formSteps.filter(item => get(item, 'type') === FORM_STEP)
    var successStep = steps[0];

    setExternalFormState(externalFormState => {
      return {
        ...externalFormState,
        step: get(widgetProps, 'step', get(successStep, 'uid', null))
      }
    })

    if (externalFormContext.onFormSubmit) {
      externalFormContext.onFormSubmit(context.session, id, {
        contactRequest,
        parent: parent,
        external: true,
        eventUid: context.eventUid,
        gatingTrigger: context.gatingTrigger,
        smart: context.smart
      });
    }
  }

  useEffect(() => {
    var formStyles = getStyles(properties, breakpoint);
    setExternalFormState(externalFormState => {
      return {
        ...externalFormState,
        formStyles: formStyles
      }
    })
  }, [deviceProperties]);

  useEffect(() => {
    setExternalFormContextState(state => {
      return {
        ...state,
        onFormSubmit: handleFormSubmit,
        parent: context.parent
      }
    });
  }, [context.parent])

  return (
    <ExternalFormWidgetContext.Provider value={externalFormContextState}>
      <div id={id}
        className={cx(className, styles.form)}
        style={widgetStyle}
        data-widget-id={id}>
        {React.Children.map(children,
          child => {
            const active = get(child, 'props.widgetProps.uid') === externalFormState.step;

            if (!active) {
              return (
                <ExternalFormView widgetProps={widgetProps} breakpoint={breakpoint} />
              )
            }

            return (
              React.cloneElement(child, {
                activeStep: active
              }, null)
            );
          })
        }
      </div>
    </ExternalFormWidgetContext.Provider>
  )
};

export const ExternalFormView = (props) => {
  const form = get(props.widgetProps, "content", null);
  const uid = get(props.widgetProps, "uid", "");

  switch (form && form.type) {
    case "hubspot":
      return <HubspotFormView form={form} uid={uid} />;
    case "marketo":
      return <MarketoFormView form={form} uid={uid} />;
    case "pardot":
      return <PardotFormView form={form} uid={uid} widget={props.widgetProps} breakpoint={props.breakpoint} />;
    case "external":
      return <ExternalForm form={form} uid={uid} widget={props.widgetProps} breakpoint={props.breakpoint} />;
    default:
      return null;
  }
}

/**
 * Get Styles for the properties
*/
function getStyles(properties, breakpoint) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = get(properties, `desktop`);
  const deviceProperties = get(properties, `${breakpoint}`);

  const font = get(defaultProperties, 'font', {});

  //font-color
  const colorRgb = get(font, 'color', null);
  const color = getColorStyles(colorRgb);
  styles = {
    ...styles,
    ...color
  }

  //font-family
  const fontFamily = get(font, 'fontFamily', 'inherit');
  styles = {
    ...styles,
    fontFamily: injectAktivFallback(fontFamily)
  }

  //font-size
  const fontSizeString = get(deviceProperties, 'regular.font.fontSize', null);
  var fontSize = 'inherit'
  if (fontSizeString) {
    fontSize = parseInt(fontSizeString) + 'px';
  }

  styles['fontSize'] = fontSize;


  //padding
  const paddingString = get(deviceProperties, 'padding', null)
  var padding = '20'
  if (paddingString) {
    padding = parseInt(paddingString) + 'px';
  }

  styles['padding'] = padding;


  //font-style
  const bold = get(font, 'style.bold', false);
  if (bold) {
    styles['fontWeight'] = 'bold';
  }

  const italic = get(font, 'style.italic', false);
  if (italic) {
    styles['fontStyle'] = 'italic';
  }

  const underline = get(font, 'style.underline', false);
  if (underline) {
    styles['textDecoration'] = 'underline';
  }

  //background
  const backgroundRgb = get(defaultProperties, 'background', null);
  var background = 'inherit';
  if (backgroundRgb) {
    background = `rgba(${backgroundRgb.r}, ${backgroundRgb.g}, ${backgroundRgb.b}, ${backgroundRgb.a})`;
  }

  styles['backgroundColor'] = background;

  //border
  const border = get(defaultProperties, 'border', {});
  const borderStyle = getBorderStyles(border);
  styles = {
    ...styles,
    ...borderStyle
  }

  //alignment
  const alignment = get(deviceProperties, 'alignment', {});

  var justifyContent = get(alignment, 'justifyContent', 'left');
  styles['justifyContent'] = justifyContent;

  var flexDirection = get(alignment, 'flexDrection', 'column');
  styles['flexDirection'] = flexDirection;

  var alignItems = get(alignment, 'alignItems', 'start');
  styles['alignItems'] = alignItems;

  return styles;
}

export function getColorStyles(value) {
  if (!value) {
    return null;
  }

  const colorRgb = value;

  var color = 'inherit';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  var styles = {
    color: color
  };

  return styles;
}

export function getBorderStyles(value) {
  if (!value) {
    return null;
  }

  var borderStyles = {}

  const topLeft = get(value, 'radius.topLeft', null);
  const topRight = get(value, 'radius.topRight', null);
  const bottomLeft = get(value, 'radius.bottomLeft', null);
  const bottomRight = get(value, 'radius.bottomRight', null);

  if (topLeft) {
    borderStyles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
  }

  if (topRight) {
    borderStyles['borderTopRightRadius'] = parseInt(topRight) + 'px';
  }

  if (bottomLeft) {
    borderStyles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
  }

  if (bottomRight) {
    borderStyles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
  }

  const colorRgb = get(value, 'color');

  var color = 'transparent';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  borderStyles['borderColor'] = color;

  const borderWidthString = get(value, 'width', 0);
  var borderWidget = parseInt(borderWidthString) + 'px';

  borderStyles['borderWidth'] = borderWidget;

  return borderStyles;
}

export { ExternalFormWidget };
